// src/config/api.ts
// Get base URL from environment variables
const envApiUrl = import.meta.env.VITE_API_URL || 
                  import.meta.env.VITE_API_BASE || 
                  import.meta.env.REACT_APP_API_URL;

// Determine environment
const isProduction = typeof window !== 'undefined' && (
  window.location.hostname === 'www.master-ji.com' || 
  window.location.hostname === 'master-ji.com' ||
  window.location.hostname === 'masterji.vercel.app'
);

const isStaging = typeof window !== 'undefined' && (
  window.location.hostname.includes('masterji-staging') ||
  window.location.hostname.includes('staging')
);

// Define fallback URLs for each environment
const getFallbackUrls = () => {
  if (isProduction) {
    return [
      'https://masterji-production.up.railway.app',
      'https://api.master-ji.com'
    ];
  } else if (isStaging) {
    return [
      'https://backend-staging-f22e.up.railway.app'
      // Removed api-staging.master-ji.com as it's not configured/serving the backend
    ];
  } else {
    return [
      'http://localhost:8000',
      'https://backend-staging-f22e.up.railway.app'
      // Removed api-staging.master-ji.com as it's not configured/serving the backend
    ];
  }
};

// Set API base URL with fallback support
let baseUrl: string;
let fallbackUrls: string[] = [];

if (envApiUrl) {
  baseUrl = envApiUrl;
  fallbackUrls = getFallbackUrls();
  console.log('🔧 Using API URL from environment:', baseUrl);
  console.log('🔧 Fallback URLs available:', fallbackUrls);
} else {
  fallbackUrls = getFallbackUrls();
  baseUrl = fallbackUrls[0]; // Use primary URL
  console.log('🔧 Using primary URL:', baseUrl);
  console.log('🔧 Fallback URLs available:', fallbackUrls);
}

// Force HTTPS for all production environments (staging and production)
if (baseUrl.includes('railway.app') || baseUrl.includes('vercel.app')) {
  if (baseUrl.startsWith('http://')) {
    baseUrl = baseUrl.replace('http://', 'https://');
    console.warn('Converted HTTP to HTTPS for security:', baseUrl);
  }
  // Ensure it's HTTPS
  if (!baseUrl.startsWith('https://')) {
    baseUrl = 'https://' + baseUrl.replace(/^https?:\/\//, '');
    console.warn('Forced HTTPS for production environment:', baseUrl);
  }
}

if (!/^https?:\/\//i.test(baseUrl)) {
  console.warn('VITE_API_URL must include protocol, using fallback');
}

// Detect misconfiguration where API points to the frontend host BEFORE exporting
if (typeof window !== 'undefined') {
  try {
    const currentHost = window.location.host;
    const parsed = new URL(baseUrl.startsWith('http') ? baseUrl : `https://${baseUrl}`);
    if (parsed.host === currentHost) {
      console.warn('VITE_API_URL points to the frontend host. Using fallback backend URL.');
      baseUrl = isProduction ? 'https://masterji-production.up.railway.app' : 'https://api-staging.master-ji.com';
    }
  } catch (_e) {
    console.warn('API_BASE host check failed:', e);
  }
}

// Debug logging for production
if (typeof window !== 'undefined') {
  console.log('API Configuration Debug:', {
    envApiUrl: envApiUrl,
    baseUrl: baseUrl,
    isProduction: isProduction,
    isStaging: isStaging,
    environment: import.meta.env.MODE,
    hostname: window.location.hostname,
    allEnvVars: {
      VITE_API_URL: import.meta.env.VITE_API_URL,
      VITE_API_BASE: import.meta.env.VITE_API_BASE,
      REACT_APP_API_URL: import.meta.env.REACT_APP_API_URL,
      MODE: import.meta.env.MODE
    }
  });

  // Force HTTPS in browser console for debugging
  if (!baseUrl.startsWith('https://')) {
    console.error('CRITICAL: API_BASE is not HTTPS!', baseUrl);
    console.error('This will cause mixed content errors!');
  }
}

// WebSocket URL
const wsUrl = baseUrl.replace('https://', 'wss://').replace('http://', 'ws://');

export const API_BASE = baseUrl.replace(/\/+$/, ''); // no trailing slash
export const SOCKET_URL = wsUrl;

// Function to build API URLs
export const apiUrl = (path: string) => {
  return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Fallback fetch function with automatic failover
export const fetchWithFallback = async (path: string, init?: RequestInit): Promise<Response> => {
  const urls = fallbackUrls.map(url => `${url}${path.startsWith('/') ? '' : '/'}${path}`);
  
  let lastError: Error | null = null;
  
  for (let i = 0; i < urls.length; i++) {
    const url = urls[i];
    try {
      console.log(`🔧 Trying API URL ${i + 1}/${urls.length}:`, url);
      const response = await fetch(url, init);
      
      // If we get a response (even if it's an error), use it
      if (response.status !== 0) {
        if (i > 0) {
          console.log(`✅ Successfully connected to fallback URL:`, url);
        }
        return response;
      }
    } catch (_error) {
      console.warn(`❌ Failed to connect to ${url}:`, error);
      lastError = error as Error;
      
      // If this is not the last URL, try the next one
      if (i < urls.length - 1) {
        console.log(`🔄 Trying next fallback URL...`);
        continue;
      }
    }
  }
  
  // If all URLs failed, throw the last error
  throw lastError || new Error('All API endpoints failed');
};

// Export fallback URLs for debugging
export const FALLBACK_URLS = fallbackUrls;
